import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatPrice } from "@/lib/api";
import api from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Plus, Minus, ShieldCheck, ArrowRight } from "lucide-react";

export default function Cart() {
  const { items, remove, updateQty, subtotal, count } = useCart();
  const [email, setEmail] = useState("");
  const [checkingOut, setCheckingOut] = useState(false);
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const shipping = subtotal >= 150 ? 0 : subtotal > 0 ? 12 : 0;
  const total = subtotal + shipping;

  const checkout = async () => {
    if (items.length === 0) return;
    if (!email) { toast.error("Please provide an email for order updates."); return; }
    setCheckingOut(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        items: items.map(({ product_id, quantity }) => ({ product_id, quantity })),
        origin_url: window.location.origin,
        email,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Could not start checkout. " + (e?.response?.data?.detail || ""));
      setCheckingOut(false);
    }
  };

  useEffect(() => {
    if (params.get("cancelled")) toast.info("Checkout cancelled.");
    if (params.get("checkout") === "1") {
      // scroll to email
      const el = document.getElementById("cart-checkout-block");
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  }, [params]);

  return (
    <div data-testid="cart-page" className="bg-slate-50 min-h-screen">
      <div className="max-w-6xl mx-auto px-4 lg:px-8 py-10">
        <h1 className="font-heading text-3xl lg:text-4xl font-bold text-slate-900">Your Cart</h1>
        {items.length === 0 ? (
          <div className="mt-10 rounded-xl border border-slate-200 bg-white p-10 text-center">
            <div className="text-slate-500 mb-4">Your cart is empty.</div>
            <Button asChild data-testid="cart-empty-shop"><Link to="/shop">Browse products</Link></Button>
          </div>
        ) : (
          <div className="mt-6 grid lg:grid-cols-[1fr_360px] gap-6">
            <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
              {items.map((it) => (
                <div key={it.product_id} className="p-4 flex gap-4">
                  <Link to={`/product/${it.slug}`} className="w-24 h-24 bg-slate-50 rounded-md overflow-hidden shrink-0">
                    <img src={it.image} alt="" className="w-full h-full object-contain" />
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link to={`/product/${it.slug}`} className="font-heading font-semibold text-slate-900 line-clamp-2">{it.title}</Link>
                    <div className="text-xs font-mono text-slate-400 mt-1">SKU {it.sku}</div>
                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex items-center border border-slate-200 rounded-md">
                        <button aria-label="Decrease" onClick={() => updateQty(it.product_id, it.quantity - 1)} className="w-8 h-8 hover:bg-slate-100 flex items-center justify-center"><Minus className="w-3 h-3" /></button>
                        <span className="w-10 text-center font-mono">{it.quantity}</span>
                        <button aria-label="Increase" onClick={() => updateQty(it.product_id, it.quantity + 1)} className="w-8 h-8 hover:bg-slate-100 flex items-center justify-center"><Plus className="w-3 h-3" /></button>
                      </div>
                      <div className="font-heading font-semibold text-slate-900">{formatPrice(it.price * it.quantity)}</div>
                    </div>
                  </div>
                  <button aria-label="Remove" onClick={() => remove(it.product_id)} className="text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>

            <div id="cart-checkout-block" className="rounded-xl border border-slate-200 bg-white p-5 h-max sticky top-24">
              <h3 className="font-heading font-semibold text-slate-900">Order summary</h3>
              <div className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between text-slate-600"><span>Items ({count})</span><span>{formatPrice(subtotal)}</span></div>
                <div className="flex justify-between text-slate-600"><span>Shipping</span><span>{shipping === 0 ? "Free" : formatPrice(shipping)}</span></div>
                <div className="flex justify-between text-slate-600"><span>Estimated tax</span><span>Calculated at checkout</span></div>
                <div className="border-t border-slate-100 pt-3 mt-3 flex justify-between text-slate-900 font-heading font-semibold text-lg"><span>Total</span><span>{formatPrice(total)}</span></div>
              </div>
              <div className="mt-4">
                <label className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Email for updates</label>
                <Input data-testid="cart-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="mt-1.5" />
              </div>
              <Button data-testid="cart-checkout" onClick={checkout} disabled={checkingOut} className="mt-4 w-full h-11 bg-[#0055FF] hover:bg-[#0044CC]">
                {checkingOut ? "Redirecting…" : <>Secure checkout <ArrowRight className="w-4 h-4 ml-1" /></>}
              </Button>
              <div className="mt-3 flex items-center gap-1.5 text-[11px] font-mono text-slate-500 uppercase tracking-widest">
                <ShieldCheck className="w-3 h-3" /> Secure payment via Stripe · SSL encrypted
              </div>
              <div className="mt-2 text-[11px] text-slate-500">Free shipping on orders over $150.</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
